import customGetProductCategories from "@hardware/Store/Handlers/Utilities/CustomGetProductCategories";
import { setGA4Event } from "@hardware/Store/Handlers/Utilities/CustomTracking";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import wrapInContainerStyles from "@insite/client-framework/Common/wrapInContainerStyles";
import {
    HasProductContext,
    ProductContextModel,
    withProductContext,
} from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { CartLineModel, OrderLineModel } from "@insite/client-framework/Types/ApiModels";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import React, { useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    product: CartLineModel | ProductContextModel | OrderLineModel;
    onClick?: React.EventHandler<React.MouseEvent>;
    extendedStyles?: ProductDescriptionStyles;
}

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => {
    const {
        pages: {
            productList: {
                productFilters: { pageCategoryId },
            },
        },
    } = state;

    return {
        pageCategoryId,
    };
};

const mapDispatchToProps = {
    customGetProductCategories,
};

type Props = ReturnType<typeof mapStateToProps> &
    OwnProps &
    ResolveThunks<typeof mapDispatchToProps> &
    HasProductContext;

export interface ProductDescriptionStyles {
    productDetailLink?: LinkPresentationProps;
    productDetailDescription?: TypographyProps;
}

export const productDescriptionStyles: ProductDescriptionStyles = {
    productDetailLink: {
        css: css`
            width: 100%;
        `,
        typographyProps: {
            css: css`
                width: 100%;
                ${wrapInContainerStyles}
            `,
        },
    },
};

const ProductDescription = ({
    product,
    onClick,
    extendedStyles,
    // Verndale: Customization
    productContext,
    pageCategoryId,
    customGetProductCategories,
}: Props) => {
    const [styles] = useState(() => mergeToNew(productDescriptionStyles, extendedStyles));

    const description =
        "product" in product
            ? product.product.productTitle
            : product.shortDescription || ("description" in product ? product.description : "");
    const productDetailPath = "product" in product ? product.productInfo.productDetailPath : product.productUri;

    // Verndale: Custom tracking
    const customClickHandler = (e: React.MouseEvent) => {
        if (onClick) {
            onClick(e);
        }

        // if product is of type CartLineModel, then we need to get the productId from pricing object
        // else if is orderLineModel, then we need to get the productId property, else we can get the productId from productInfo object

        let productId = "";

        if ("product" in product) {
            productId = (product as ProductContextModel).productInfo.productId;
        } else {
            productId = (product as CartLineModel).productId!;
        }

        customGetProductCategories({
            productIds: [productId],
            type: "product",
            onSuccess: data => {
                if (data) {
                    const categories = data.productProperties[0].categories;
                    setGA4Event("select_item", [product], {
                        categories,
                        pageCategoryId,
                    });
                }
            },
        });
    };
    // Verndale: End custom tracking

    return productDetailPath && (!("isActiveProduct" in product) || product.isActiveProduct) ? (
        <Link
            {...styles.productDetailLink}
            href={productDetailPath}
            alt={`See product details for ${description}`}
            onClick={customClickHandler} // Verndale: Custom tracking
            data-test-selector="productDescriptionLink"
        >
            {description}
        </Link>
    ) : (
        <Typography {...styles.productDetailDescription}>{description}</Typography>
    );
};

export default withProductContext(connect(mapStateToProps, mapDispatchToProps)(ProductDescription as any) as any);
